// The design, and the button that makes it croppable.
//
// Dragging is a mode rather than the image's resting state: the preview is mostly there to be
// looked at, and an image that slides under any stray drag is one you cannot scroll past on a
// touchscreen. Read-only without `onChange` — no button, no handlers.
import React, { useRef, useState } from 'react';
import { dragFocus, focusPosition } from './format';

const FocusImage = ({ src, focus, onChange, alt = '' }) => {
  const [adjusting, setAdjusting] = useState(false);
  const from = useRef(null);
  const live = !!onChange && adjusting;

  const down = (event) => {
    if (!live) return;
    // Pointer capture, so a drag that leaves the little box keeps reporting until the button is
    // released — without it the crop stops the moment the cursor crosses the phone's edge.
    event.currentTarget.setPointerCapture(event.pointerId);
    from.current = { x: event.clientX, y: event.clientY, focus };
  };

  const move = (event) => {
    if (!from.current) return;
    const img = event.currentTarget;
    onChange(
      dragFocus({
        focus: from.current.focus,
        dx: event.clientX - from.current.x,
        dy: event.clientY - from.current.y,
        box: { w: img.clientWidth, h: img.clientHeight },
        natural: { w: img.naturalWidth, h: img.naturalHeight },
      }),
    );
  };

  const up = (event) => {
    if (!from.current) return;
    from.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  return (
    <div className={`focus ${live ? 'on' : ''}`.trim()}>
      <img
        src={src}
        alt={alt}
        className={live ? 'drag' : ''}
        style={{ objectPosition: focusPosition(focus) }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        draggable={false}
      />
      {onChange && (
        <>
          <button
            type="button"
            className={`focus-btn ${adjusting ? 'on' : ''}`.trim()}
            onClick={() => setAdjusting((was) => !was)}
          >
            {adjusting ? 'Done' : 'Reposition'}
          </button>
          {adjusting && <span className="focus-cap">Drag to choose what stays in frame</span>}
        </>
      )}
    </div>
  );
};

export default FocusImage;
